import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { kr } from '../../lib/money';
import type { RouterOutputs } from '../../lib/trpc';
import { Meter, Panel } from '../common/Panel';

type Summary = RouterOutputs['period']['summary'];
type Envelope = Summary['envelopes']['items'][number];

const TONE = {
  under: 'positive',
  // biome-ignore lint/style/useNamingConvention: mirrors the API's status value
  on_track: 'positive',
  over: 'warning',
  exceeded: 'negative',
} as const;

const STATUS_LABEL = {
  under: 'Under takten',
  // biome-ignore lint/style/useNamingConvention: mirrors the API's status value
  on_track: 'I fas',
  over: 'Över takten',
  exceeded: 'Över gränsen',
} as const;

/** Each monthly limit: spent vs limit, with a tick where an even pace would be today. */
export function LimitsPaceCard({ summary }: { summary: Summary }) {
  const { period } = summary;
  const limits = summary.envelopes.items.filter((e) => e.budgeted > 0);
  const timeShare = period.daysTotal > 0 ? period.daysElapsed / period.daysTotal : 1;
  const totalBudgeted = limits.reduce((s, e) => s + e.budgeted, 0);
  const totalSpent = limits.reduce((s, e) => s + e.spent, 0);

  if (limits.length === 0) {
    return (
      <Panel title="Gränser">
        <p className="text-sm text-muted-foreground">
          Du har inga gränser än. Sätt en månadsgräns för t.ex. mat eller restaurang under{' '}
          <Link to="/plan" className="font-medium text-primary hover:underline">
            Plan
          </Link>
          , så ser du här hur takten håller.
        </p>
      </Panel>
    );
  }

  return (
    <Panel
      title="Gränser"
      action={
        <Link to="/plan" className="font-medium text-primary hover:underline">
          Ändra
        </Link>
      }
      description={
        period.isCurrent
          ? `${Math.round(timeShare * 100)} % av perioden har gått`
          : `${kr(totalSpent)} av ${kr(totalBudgeted)}`
      }
    >
      <ul className="space-y-4">
        {limits.map((limit) => (
          <LimitRow
            key={limit.budgetId}
            limit={limit}
            marker={period.isCurrent ? timeShare : undefined}
            showPace={period.isCurrent}
          />
        ))}
      </ul>
      {period.isCurrent && (
        <p className="mt-4 text-xs text-muted-foreground">
          Strecket visar var du borde ligga idag om pengarna sprids jämnt över perioden.
        </p>
      )}
    </Panel>
  );
}

function LimitRow({
  limit,
  marker,
  showPace,
}: {
  limit: Envelope;
  marker: number | undefined;
  showPace: boolean;
}) {
  const over = limit.remaining < 0;
  // Outside the running period, pace is meaningless; only over/under the limit counts
  const status = showPace || limit.status === 'exceeded' ? limit.status : 'under';
  return (
    <li>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="truncate text-sm font-medium">{limit.name}</span>
        <span
          className={cn(
            'shrink-0 text-sm tabular-nums',
            over ? 'text-negative' : 'text-muted-foreground',
          )}
        >
          {over ? `${kr(-limit.remaining)} över` : `${kr(limit.remaining)} kvar`}
        </span>
      </div>
      <Meter
        value={limit.spent / limit.budgeted}
        marker={marker}
        tone={TONE[status]}
        label={`${limit.name}: ${kr(limit.spent)} av ${kr(limit.budgeted)}`}
      />
      <div className="mt-1 flex justify-between gap-3 text-xs text-muted-foreground tabular-nums">
        <span>
          {kr(limit.spent)} av {kr(limit.budgeted)}
        </span>
        {showPace && limit.status !== 'exceeded' && (
          <span className={cn(limit.status === 'over' && 'text-warning')}>
            {STATUS_LABEL[limit.status]}
          </span>
        )}
      </div>
    </li>
  );
}
